package com.far.suller.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.far.suller.models.Theme
import com.far.suller.styles.InputStyle
import com.far.suller.styles.MainButtonStyle
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

@Composable
fun contactForm(breakpoint: Breakpoint) {
    val isDesktop = breakpoint >= Breakpoint.MD
    val inputWidth = if (isDesktop) 500.px else 100.percent

    // Submit cooldown state
    var submitCooldown by remember { mutableStateOf(false) }
    var buttonLabel by remember { mutableStateOf("Submit") }
    val scope = rememberCoroutineScope()

    // ── Name validation ───────────────────────────────────────────────────
    // Min 2 chars, letters and spaces only
    var nameValue by remember { mutableStateOf("") }
    var nameTouched by remember { mutableStateOf(false) }
    val isNameValid = nameValue.length >= 2 && nameValue.matches(Regex("^[A-Za-z ]+$"))
    val showNameError = nameTouched && nameValue.isNotEmpty() && !isNameValid
    val showNameEmpty = nameTouched && nameValue.isEmpty()

    // ── Email validation ──────────────────────────────────────────────────
    val emailRegex = Regex("^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}$")
    var emailValue by remember { mutableStateOf("") }
    var emailTouched by remember { mutableStateOf(false) }
    val isEmailValid = emailValue.isNotEmpty() && emailRegex.matches(emailValue)
    val showEmailError = emailTouched && emailValue.isNotEmpty() && !isEmailValid
    val showEmailEmpty = emailTouched && emailValue.isEmpty()

    // ── Message validation ────────────────────────────────────────────────
    // Min 10 chars
    var messageValue by remember { mutableStateOf("") }
    var messageTouched by remember { mutableStateOf(false) }
    val isMessageValid = messageValue.length >= 10
    val showMessageError = messageTouched && messageValue.isNotEmpty() && !isMessageValid
    val showMessageEmpty = messageTouched && messageValue.isEmpty()

    // ── Global gate — Submit only when all fields pass ────────────────────
    val canSubmit = !submitCooldown && isNameValid && isEmailValid && isMessageValid

    Form(
        action = "https://formspree.io/f/xeqwplzj",
        attrs = Modifier
            .attrsModifier { attr("method", "POST") }
            .fillMaxWidth()
            .toAttrs(),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            // Honeypot — bots fill it, Formspree discards the submission
            Input(
                type = InputType.Text,
                attrs = Modifier
                    .attrsModifier {
                        attr("name", "_gotcha")
                        attr("tabindex", "-1")
                        attr("autocomplete", "off")
                    }
                    .styleModifier {
                        property("display", "none")
                        property("position", "absolute")
                        property("left", "-9999px")
                    }
                    .toAttrs(),
            )

            // ── Name Field ────────────────────────────────────────────────
            Column(
                modifier = Modifier.width(inputWidth).margin(bottom = 20.px),
                horizontalAlignment = Alignment.Start,
            ) {
                Label(
                    attrs = Modifier
                        .classNames("form-label")
                        .margin(bottom = 6.px)
                        .color(Colors.White)
                        .fontWeight(FontWeight.Bold)
                        .fontSize(14.px)
                        .toAttrs(),
                    forId = "inputName",
                ) { Text("Name") }

                Input(
                    type = InputType.Text,
                    attrs = InputStyle
                        .toModifier()
                        .id("inputName")
                        .classNames("form-control")
                        .fillMaxWidth()
                        .backgroundColor(Color.rgba(255, 255, 255, 0.05f))
                        .color(Colors.White)
                        .border(
                            1.px,
                            org.jetbrains.compose.web.css.LineStyle.Solid,
                            when {
                                showNameError || showNameEmpty -> Color.rgb(239, 68, 68)
                                nameTouched && isNameValid -> Theme.Primary.rgb
                                else -> Color.rgba(255, 255, 255, 0.1f)
                            },
                        )
                        .borderRadius(8.px)
                        .padding(leftRight = 16.px, topBottom = 12.px)
                        .boxShadow(0.px, 0.px, 0.px, 0.px, null)
                        .attrsModifier {
                            attr("placeholder", "Full Name")
                            attr("name", "name")
                            attr("required", "true")
                            attr("maxlength", "100")
                            addEventListener("input") { event ->
                                val target = event.target as? org.w3c.dom.HTMLInputElement
                                nameValue = target?.value ?: ""
                                nameTouched = true
                            }
                        }
                        .toAttrs(),
                )

                if (showNameEmpty) {
                    P(
                        attrs = Modifier
                            .margin(top = 4.px, bottom = 0.px)
                            .color(Color.rgb(239, 68, 68))
                            .fontSize(12.px)
                            .toAttrs(),
                    ) { Text("\u26a0 Name is required") }
                } else if (showNameError) {
                    P(
                        attrs = Modifier
                            .margin(top = 4.px, bottom = 0.px)
                            .color(Color.rgb(239, 68, 68))
                            .fontSize(12.px)
                            .toAttrs(),
                    ) { Text("\u26a0 Name must be at least 2 characters and contain only letters") }
                }
            }

            // ── Email Field ───────────────────────────────────────────────
            Column(
                modifier = Modifier.width(inputWidth).margin(bottom = 20.px),
                horizontalAlignment = Alignment.Start,
            ) {
                Label(
                    attrs = Modifier
                        .classNames("form-label")
                        .margin(bottom = 2.px)
                        .color(Colors.White)
                        .fontWeight(FontWeight.Bold)
                        .fontSize(14.px)
                        .toAttrs(),
                    forId = "inputEmail",
                ) { Text("Email") }

                P(
                    attrs = Modifier
                        .margin(top = 0.px, bottom = 8.px)
                        .color(Color.rgba(245, 158, 11, 0.7f))
                        .fontSize(11.px)
                        .toAttrs(),
                ) { Text("(Your email \u2014 so I can reply to you)") }

                Input(
                    type = InputType.Email,
                    attrs = InputStyle
                        .toModifier()
                        .id("inputEmail")
                        .classNames("form-control")
                        .fillMaxWidth()
                        .backgroundColor(Color.rgba(255, 255, 255, 0.05f))
                        .color(Colors.White)
                        .border(
                            1.px,
                            org.jetbrains.compose.web.css.LineStyle.Solid,
                            when {
                                showEmailError || showEmailEmpty -> Color.rgb(239, 68, 68)
                                emailTouched && isEmailValid -> Theme.Primary.rgb
                                else -> Color.rgba(255, 255, 255, 0.1f)
                            },
                        )
                        .borderRadius(8.px)
                        .padding(leftRight = 16.px, topBottom = 12.px)
                        .boxShadow(0.px, 0.px, 0.px, 0.px, null)
                        .attrsModifier {
                            attr("placeholder", "your@email.com")
                            attr("name", "email")
                            attr("required", "true")
                            attr("maxlength", "60")
                            // No attr("value") binding — causes cursor-jump on recompose
                            addEventListener("input") { event ->
                                val target = event.target as? org.w3c.dom.HTMLInputElement
                                emailValue = target?.value ?: ""
                                emailTouched = true
                            }
                        }
                        .toAttrs(),
                )

                if (showEmailEmpty) {
                    P(
                        attrs = Modifier
                            .margin(top = 4.px, bottom = 0.px)
                            .color(Color.rgb(239, 68, 68))
                            .fontSize(12.px)
                            .toAttrs(),
                    ) { Text("\u26a0 Email is required") }
                } else if (showEmailError) {
                    P(
                        attrs = Modifier
                            .margin(top = 4.px, bottom = 0.px)
                            .color(Color.rgb(239, 68, 68))
                            .fontSize(12.px)
                            .toAttrs(),
                    ) { Text("\u26a0 Please enter a valid email address (e.g. name@example.com)") }
                }
            }

            // ── Message Field ─────────────────────────────────────────────
            Column(
                modifier = Modifier.width(inputWidth).margin(bottom = 32.px),
                horizontalAlignment = Alignment.Start,
            ) {
                Label(
                    attrs = Modifier
                        .classNames("form-label")
                        .margin(bottom = 6.px)
                        .color(Colors.White)
                        .fontWeight(FontWeight.Bold)
                        .fontSize(14.px)
                        .toAttrs(),
                    forId = "inputMessage",
                ) { Text("Message") }

                TextArea(
                    attrs = InputStyle
                        .toModifier()
                        .id("inputMessage")
                        .classNames("form-control")
                        .fillMaxWidth()
                        .height(150.px)
                        .backgroundColor(Color.rgba(255, 255, 255, 0.05f))
                        .color(Colors.White)
                        .border(
                            1.px,
                            org.jetbrains.compose.web.css.LineStyle.Solid,
                            when {
                                showMessageError || showMessageEmpty -> Color.rgb(239, 68, 68)
                                messageTouched && isMessageValid -> Theme.Primary.rgb
                                else -> Color.rgba(255, 255, 255, 0.1f)
                            },
                        )
                        .borderRadius(8.px)
                        .padding(all = 16.px)
                        .boxShadow(0.px, 0.px, 0.px, 0.px, null)
                        .attrsModifier {
                            attr("placeholder", "Your Message (min. 10 characters)")
                            attr("name", "message")
                            attr("required", "true")
                            attr("maxlength", "800")
                            addEventListener("input") { event ->
                                val target = event.target as? org.w3c.dom.HTMLTextAreaElement
                                messageValue = target?.value ?: ""
                                messageTouched = true
                            }
                        }
                        .toAttrs(),
                )

                if (showMessageEmpty) {
                    P(
                        attrs = Modifier
                            .margin(top = 4.px, bottom = 0.px)
                            .color(Color.rgb(239, 68, 68))
                            .fontSize(12.px)
                            .toAttrs(),
                    ) { Text("\u26a0 Message is required") }
                } else if (showMessageError) {
                    P(
                        attrs = Modifier
                            .margin(top = 4.px, bottom = 0.px)
                            .color(Color.rgb(239, 68, 68))
                            .fontSize(12.px)
                            .toAttrs(),
                    ) { Text("\u26a0 Message must be at least 10 characters") }
                } else if (messageTouched && isMessageValid) {
                    P(
                        attrs = Modifier
                            .margin(top = 4.px, bottom = 0.px)
                            .color(Color.rgba(255, 255, 255, 0.4f))
                            .fontSize(11.px)
                            .toAttrs(),
                    ) { Text("${messageValue.length} / 800") }
                }
            }

            // ── Submit Button ─────────────────────────────────────────────
            Box(
                modifier = Modifier.fillMaxWidth(),
                contentAlignment = Alignment.Center,
            ) {
                Button(
                    attrs = MainButtonStyle
                        .toModifier()
                        .height(48.px)
                        .minWidth(160.px)
                        .border(width = 0.px)
                        .borderRadius(r = 24.px)
                        .backgroundColor(
                            if (!canSubmit) Color.rgba(245, 158, 11, 0.35f) else Theme.Primary.rgb,
                        )
                        .color(Color.rgb(26, 10, 0))
                        .cursor(if (!canSubmit) Cursor.NotAllowed else Cursor.Pointer)
                        .fontWeight(FontWeight.Bold)
                        .attrsModifier {
                            attr("type", "submit")
                            if (!canSubmit) attr("disabled", "true")
                        }
                        .onClick {
                            if (canSubmit) {
                                buttonLabel = "Sending..."
                                submitCooldown = true
                                scope.launch {
                                    delay(2_000)
                                    buttonLabel = "Sent \u2713"
                                    delay(28_000)
                                    buttonLabel = "Submit"
                                    submitCooldown = false
                                }
                            }
                        }
                        .toAttrs(),
                ) {
                    Text(buttonLabel)
                }
            }
        }
    }
}
